import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { BookOpen, ChevronLeft, ChevronRight, CircleAlert, Play, Search, Star } from "lucide-react";
import { useState } from "react";
import { PoliticsQuestionDetailPanel } from "@/components/politics-question-detail";
import { PoliticsActiveSessionDialog } from "@/components/politics-session-dialog";
import { Button, EmptyState, LoadingLine, PageHeader, Tag } from "@/components/primitives";
import { ApiError, apiFetch } from "@/lib/api";
import {
  QUESTION_TYPE_LABELS,
  politicsSessionRequestBody,
  type PoliticsBank,
  type PoliticsQuestionListItem,
  type PoliticsQuestionTypeFilter,
  type PoliticsSession,
  type PoliticsSessionSummary,
} from "@/lib/politics";

type CollectionKind = "wrong" | "favorites";
interface CollectionSessionRequest {
  clientRequestId: string;
  bankId: string;
  source: "wrong" | "favorites";
  questionType: PoliticsQuestionTypeFilter;
  order: "sequential";
  count: number;
}

export function PoliticsCollectionPage({ kind }: { kind: CollectionKind }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [questionType, setQuestionType] = useState<PoliticsQuestionTypeFilter>("all");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeSession, setActiveSession] = useState<PoliticsSessionSummary | null>(null);
  const [pendingRequest, setPendingRequest] = useState<CollectionSessionRequest | null>(null);
  const banks = useQuery({
    queryKey: ["politics", "banks"],
    queryFn: () => apiFetch<{ banks: PoliticsBank[] }>("/politics/banks"),
  });
  const activeBankId = banks.data?.banks[0]?.id ?? "";
  const params = new URLSearchParams({ page: String(page), pageSize: "20", questionType });
  if (activeBankId) params.set("bankId", activeBankId);
  const queryKey = ["politics", kind, activeBankId, questionType, page] as const;
  const questions = useQuery({
    queryKey,
    queryFn: () => apiFetch<{ questions: PoliticsQuestionListItem[]; page: number; pageSize: number; total: number }>(
      `/politics/${kind === "wrong" ? "wrong-questions" : "favorites"}?${params}`,
    ),
    enabled: Boolean(activeBankId),
  });

  const createSession = async (request: CollectionSessionRequest, replaceActive = false) => {
    setBusy(true);
    setNotice("");
    try {
      const response = await apiFetch<{ session: PoliticsSession }>("/politics/sessions", {
        method: "POST",
        body: JSON.stringify(politicsSessionRequestBody(request, replaceActive)),
      });
      setActiveSession(null);
      setPendingRequest(null);
      await queryClient.invalidateQueries({ queryKey: ["politics"] });
      await navigate({ to: "/app/$subject/practice/$sessionId", params: { subject: "politics", sessionId: response.session.id } });
    } catch (error) {
      if (error instanceof ApiError && error.message === "active_session_exists") {
        const body = error.body as { activeSession?: PoliticsSessionSummary };
        if (body.activeSession) {
          setPendingRequest(request);
          setActiveSession(body.activeSession);
          return;
        }
      }
      setNotice(error instanceof Error && error.message === "no_questions" ? "当前没有可练习的题目。" : "练习创建失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  };
  const startSession = () => {
    const bank = banks.data?.banks[0];
    if (!bank) return;
    void createSession({
      clientRequestId: crypto.randomUUID(),
      bankId: bank.id,
      source: kind === "wrong" ? "wrong" : "favorites",
      questionType,
      order: "sequential",
      count: Math.max(1, Math.min(100, questions.data?.total ?? 20)),
    });
  };

  const title = kind === "wrong" ? "政治错题本" : "政治收藏题";
  const description = kind === "wrong"
    ? "错题会持续保留；在两个不同练习中连续答对后自动移出。"
    : "集中整理需要再次推敲或反复回看的题目。";
  const Icon = kind === "wrong" ? CircleAlert : Star;
  const totalPages = Math.max(1, Math.ceil((questions.data?.total ?? 0) / (questions.data?.pageSize ?? 20)));
  return (
    <div className="page politics-collection-page">
      <PageHeader
        title={title}
        description={description}
        action={questions.data && questions.data.total > 0
          ? <Button variant="primary" type="button" disabled={busy} onClick={startSession}><Play /> 开始练习</Button>
          : undefined}
      />
      <section className="politics-collection-toolbar">
        <div><Icon /><strong>{questions.data?.total ?? 0}</strong><span>{kind === "wrong" ? "道待巩固题目" : "道已收藏题目"}</span></div>
        <label>
          <span>题型</span>
          <select className="input" value={questionType} onChange={(event) => { setQuestionType(event.target.value as PoliticsQuestionTypeFilter); setPage(1); }}>
            {(Object.keys(QUESTION_TYPE_LABELS) as PoliticsQuestionTypeFilter[]).map((type) => <option key={type} value={type}>{QUESTION_TYPE_LABELS[type]}</option>)}
          </select>
        </label>
      </section>
      {questions.isLoading || banks.isLoading ? <LoadingLine /> : null}
      {questions.isError || banks.isError ? <p className="politics-inline-notice">列表读取失败，请稍后重试。</p> : null}
      {notice ? <p className="politics-inline-notice" role="status">{notice}</p> : null}
      {questions.data?.questions.length ? (
        <section className="politics-question-list" aria-label={title}>
          {questions.data.questions.map((question) => (
            <article key={question.id}>
              <header>
                <span>第 {question.sourceNumber} 题</span>
                <div className="tag-list">
                  <Tag tone={question.type === "multiple_choice" ? "accent" : "neutral"}>{question.type === "single_choice" ? "单选题" : "多选题"}</Tag>
                  <Tag>{question.section.name}</Tag>
                </div>
              </header>
              <h2>{question.stem}</h2>
              <footer>
                <span>{question.progress.attemptCount ? `累计作答 ${question.progress.attemptCount} 次` : "等待练习"}</span>
                <Button type="button" variant="ghost" onClick={() => setDetailId(question.id)}><Search /> 查看详情</Button>
              </footer>
            </article>
          ))}
        </section>
      ) : !questions.isLoading && !questions.isError ? (
        <EmptyState
          title={kind === "wrong" ? "当前没有待巩固错题" : "还没有收藏题目"}
          description={kind === "wrong" ? "练习中答错的题目会自动汇总到这里。" : "作答或查看解析时，可以随时收藏重点题目。"}
          action={<Button type="button" variant="primary" onClick={() => void navigate({ to: "/app/$subject/question-bank", params: { subject: "politics" } })}><BookOpen /> 前往题库</Button>}
        />
      ) : null}
      {questions.data && questions.data.total > questions.data.pageSize ? (
        <nav className="politics-pagination" aria-label="题目分页">
          <span>第 {questions.data.page} / {totalPages} 页</span>
          <div>
            <Button size="sm" type="button" aria-label="上一页" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft /></Button>
            <Button size="sm" type="button" aria-label="下一页" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}><ChevronRight /></Button>
          </div>
        </nav>
      ) : null}
      {detailId ? <PoliticsQuestionDetailPanel questionId={detailId} onClose={() => setDetailId(null)} /> : null}
      {activeSession && pendingRequest ? (
        <PoliticsActiveSessionDialog
          session={activeSession}
          busy={busy}
          onCancel={() => { setActiveSession(null); setPendingRequest(null); }}
          onResume={() => void navigate({ to: "/app/$subject/practice/$sessionId", params: { subject: "politics", sessionId: activeSession.id } })}
          onReplace={() => void createSession(pendingRequest, true)}
        />
      ) : null}
    </div>
  );
}
